import { Card, CardContent, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import ChartHeader from '~/modules/common/charts/dashboard/ChartHeader';
import { useHourChartConfig } from '~/modules/common/charts/dashboard/config';
import { BarsChartLoading } from '~/modules/common/components/DetailsPage/Charts';
import { HoursIcon } from '~/modules/common/components/Icons';
import BudgetHoursChartLegends from '../BudgetHoursChartLegends';
import { useProjectBudgetHoursChartData } from '../hooks/useProjectBudgetHoursChartData';
import { useBudgetHoursSummary } from '../hooks/useBudgetHoursSummary';
import BudgetChart from './BudgetChart';
import ProjectHoursSummaryChartHelp from './ProjectHoursSummaryChartHelp';

const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(0, 0, 2, 0),
    overflow: 'visible'
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    flexWrap: 'wrap'
  }
}));

const projectBudgetTitleId = 'projectBudgetHours';

const BudgetHoursChartTitle = (
  <span id={projectBudgetTitleId}>
    <FormattedMessage id="projectPage.budgetHours" />
  </span>
);
const BudgetHoursChartAvatar = <HoursIcon />;

export const ProjectHoursSummaryChart = ({ projectDetails }) => {
  const classes = useStyles();

  const { actualBarColor, actualLabelColor } = useHourChartConfig();
  const { projectId } = projectDetails;
  const { projectHoursSummary, isSummaryLoading } = useBudgetHoursSummary({
    projectId
  });

  const {
    estimatedDataPoints,
    actualDataPoints
  } = useProjectBudgetHoursChartData(projectHoursSummary, isSummaryLoading);

  const action = useMemo(() => <ProjectHoursSummaryChartHelp />, []);

  return (
    <Card className={classes.root}>
      <ChartHeader
        title={BudgetHoursChartTitle}
        avatar={BudgetHoursChartAvatar}
        action={action}
      />
      <CardContent>
        {isSummaryLoading ? (
          <BarsChartLoading showLegends={false} />
        ) : (
          <div className={classes.content}>
            <BudgetChart
              titleId={projectBudgetTitleId}
              estimatedDataPoints={estimatedDataPoints}
              actualDataPoints={actualDataPoints}
              actualBarColor={actualBarColor}
              actualLabelColor={actualLabelColor}
              gradientId="gradientForBudgetHours"
              maxBudgetLimit={projectHoursSummary.budgetHours}
            />
            <BudgetHoursChartLegends
              actualBarColor={actualBarColor}
              projectHoursSummary={projectHoursSummary}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

ProjectHoursSummaryChart.propTypes = {
  projectDetails: PropTypes.object
};

export default ProjectHoursSummaryChart;
